"use client"

import { useState, useEffect, useRef } from "react"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import "./MapView.css"
import { MapPin, Navigation, Plus } from "lucide-react"
import AddLocationModal from "./AddLocationModal"

// Fix for default markers in Leaflet with React - NO SHADOWS
const MapView = ({
  places,
  allVotes,
  onStartVoting,
  onBackToSwipe,
  showVotes,
  placeTypes = {},
  onShowAllPlaces,
  onAddLocation,
}) => {
  console.log("🗺️ MapView rendered with:", {
    placesCount: places.length,
    placeTypesCount: Object.keys(placeTypes).length,
    placeTypes: placeTypes,
    showVotes: showVotes,
  })
  const mapRef = useRef(null)
  const markersRef = useRef({})
  const currentLocationMarkerRef = useRef(null)
  const mapContainerRef = useRef(null)
  const [visiblePlaces, setVisiblePlaces] = useState(places)
  const [visibleCount, setVisibleCount] = useState(places.length)
  const [isLocating, setIsLocating] = useState(false)
  const [currentLocation, setCurrentLocation] = useState(null)
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  const [isShowingUserLocation, setIsShowingUserLocation] = useState(false)
  const [showAddLocationModal, setShowAddLocationModal] = useState(false)
  const [isAddingLocation, setIsAddingLocation] = useState(false)

  useEffect(() => {
    if (isSafari) {
      // Completely override Leaflet's default marker for Safari
      delete L.Icon.Default.prototype._getIconUrl
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: `/images/default.png?safari=${Date.now()}&r=${Math.random()}`,
        iconUrl: `/images/default.png?safari=${Date.now()}&r=${Math.random()}`,
        shadowUrl: null,
        iconSize: [41, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [0, 0],
      })
    } else {
      // Standard setup for other browsers
      delete L.Icon.Default.prototype._getIconUrl
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: "/images/default.png",
        iconUrl: "/images/default.png",
        shadowUrl: null,
        iconSize: [41, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [0, 0],
      })
    }
  }, [])

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return

    // Create map with simple settings
    const map = L.map(mapContainerRef.current, {
      zoomControl: true,
      attributionControl: true,
    })

    // Add tile layer
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
    }).addTo(map)

    mapRef.current = map

    // Add moveend event listener for filtering and state saving
    map.on("moveend", () => {
      handleMapMove()
    })

    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [])

  // Add markers and fit bounds when places change
  useEffect(() => {
    console.log("🗺️ MapView received places:", {
      placesCount: places.length,
      mapExists: !!mapRef.current,
    })

    if (!mapRef.current || !places.length) return

    // Clear existing markers
    const existingMarkerCount = Object.keys(markersRef.current).length
    Object.values(markersRef.current).forEach((marker) => {
      mapRef.current?.removeLayer(marker)
    })
    markersRef.current = {}
    console.log(`🗑️ Cleared ${existingMarkerCount} existing markers`)

    // Add new markers for the filtered places we received
    places.forEach((place, index) => {
      console.log(`📍 Creating marker ${index + 1}/${places.length} for: ${place.name}`)
      const marker = createMarkerForPlace(place)
      marker.addTo(mapRef.current)
      markersRef.current[place.id] = marker
    })

    console.log(`✅ Created ${places.length} new markers`)

    // Always fit map to show all markers on load/refresh
    if (places.length > 0) {
      const bounds = L.latLngBounds(places.map((place) => [place.lat, place.lng]))
      mapRef.current.fitBounds(bounds, { padding: [20, 20] })
      console.log("🎯 Fitted map bounds to show all places")
    }

    // Initial filter
    handleMapMove()
  }, [places])

  // Update markers when votes change
  useEffect(() => {
    Object.entries(markersRef.current).forEach(([placeId, marker]) => {
      const place = places.find((p) => p.id === placeId)
      if (place) {
        updateMarkerAppearance(marker, place)
      }
    })
  }, [allVotes, showVotes, places])

  // Handle current location functionality
  const handleLocationClick = () => {
    if (isShowingUserLocation) {
      // Switch back to showing all places
      if (mapRef.current && places.length > 0) {
        const bounds = L.latLngBounds(places.map((place) => [place.lat, place.lng]))
        mapRef.current.fitBounds(bounds, { padding: [20, 20] })
        setIsShowingUserLocation(false)
      }
    } else {
      // Show user location
      if (!navigator.geolocation) {
        alert("Geolocation is not supported by this browser.")
        return
      }

      setIsLocating(true)

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          const userLocation = [latitude, longitude]

          setCurrentLocation({ lat: latitude, lng: longitude })
          setIsLocating(false)
          setIsShowingUserLocation(true)

          if (mapRef.current) {
            // Remove existing current location marker if it exists
            if (currentLocationMarkerRef.current) {
              mapRef.current.removeLayer(currentLocationMarkerRef.current)
            }

            // Create a custom icon for current location
            const currentLocationIcon = L.divIcon({
              html: `
                <div class="current-location-marker">
                  <div class="location-pulse"></div>
                  <div class="location-dot"></div>
                </div>
              `,
              className: "current-location-icon",
              iconSize: [20, 20],
              iconAnchor: [10, 10],
            })

            // Add current location marker
            currentLocationMarkerRef.current = L.marker(userLocation, {
              icon: currentLocationIcon,
            })
              .addTo(mapRef.current)
              .bindPopup("📍 Your current location")

            // Zoom to current location
            mapRef.current.setView(userLocation, 16, {
              animate: true,
              duration: 1,
            })
          }
        },
        (error) => {
          setIsLocating(false)
          let errorMessage = "Unable to get your location. "

          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage += "Location access was denied. Please enable location permissions and try again."
              break
            case error.POSITION_UNAVAILABLE:
              errorMessage += "Location information is unavailable."
              break
            case error.TIMEOUT:
              errorMessage += "Location request timed out."
              break
            default:
              errorMessage += "An unknown error occurred."
              break
          }

          alert(errorMessage)
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        },
      )
    }
  }

  // Handle show all places
  const handleHardReset = () => {
    console.log("🔄 Performing hard reset of list")

    // Reset type filter to show all places
    if (onShowAllPlaces) {
      onShowAllPlaces()
    }

    // Reset user location state
    setIsShowingUserLocation(false)
    if (currentLocationMarkerRef.current && mapRef.current) {
      mapRef.current.removeLayer(currentLocationMarkerRef.current)
      currentLocationMarkerRef.current = null
    }

    // Zoom map to show all places and force bounds update
    if (mapRef.current && places.length > 0) {
      const bounds = L.latLngBounds(places.map((place) => [place.lat, place.lng]))

      // Use fitBounds with a callback to ensure it completes
      mapRef.current.fitBounds(bounds, {
        padding: [20, 20],
        animate: false, // Disable animation for immediate effect
      })

      // Force immediate recalculation of visible places
      setTimeout(() => {
        handleMapMove()
        console.log("🔄 Forced map move calculation after hard reset")
      }, 100)
    }

    // Reset voting to start from the beginning
    localStorage.setItem("triptoi-reset-voting", "true")

    // Show confirmation
    alert(
      `🔄 Hard reset complete!\n\n📍 All ${places.length} places are now visible\n🗳️ Voting will restart from the beginning`,
    )
  }

  // Handle add location
  const handleAddLocationClick = () => {
    setShowAddLocationModal(true)
  }

  const handleAddLocation = async (url) => {
    setIsAddingLocation(true)
    try {
      await onAddLocation(url)
    } finally {
      setIsAddingLocation(false)
    }
  }

  const createMarkerForPlace = (place) => {
    const placeKey = `${place.lat},${place.lng}`
    const voteData = allVotes[placeKey]
    const placeType = placeTypes[placeKey]

    console.log(`🏷️ Creating marker for "${place.name}":`, {
      placeKey,
      placeType,
      hasVoteData: !!voteData,
      voteTotal: voteData?.total || 0,
      showVotes,
      isLocalhost: window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1",
    })

    let icon
    const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"

    // Always use the appropriate place type icon as the base
    if (isLocalhost || !placeType) {
      console.log(`📍 Using default icon for ${place.name} (localhost: ${isLocalhost}, placeType: ${placeType})`)
      icon = new L.Icon.Default()
    } else {
      // Use custom icons for production
      const cacheParam = isSafari ? `?safari=${Date.now()}&r=${Math.random()}` : `?t=${Date.now()}`

      if (placeType === "restaurant") {
        console.log(`🍽️ Using restaurant icon for ${place.name}`)
        icon = new L.Icon({
          iconUrl: `/images/restaurant.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "activity") {
        console.log(`🎯 Using activity icon for ${place.name}`)
        icon = new L.Icon({
          iconUrl: `/images/attraction.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "lodging") {
        console.log(`🏨 Using lodging icon for ${place.name}`)
        icon = new L.Icon({
          iconUrl: `/images/accommodation.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "nature") {
        console.log(`🌿 Using nature icon for ${place.name}`)
        icon = new L.Icon({
          iconUrl: `/images/nature.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "airport") {
        console.log(`✈️ Using airport icon for ${place.name}`)
        icon = new L.Icon({
          iconUrl: `/images/airport.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "coffee") {
        console.log(`☕ Using coffee icon for ${place.name}`)
        icon = new L.Icon({
          iconUrl: `/images/coffee.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "luggage") {
        console.log(`🧳 Using luggage icon for ${place.name}`)
        icon = new L.Icon({
          iconUrl: `/images/luggage.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "drinks") {
        console.log(`🍹 Using drinks icon for ${place.name}`)
        icon = new L.Icon({
          iconUrl: `/images/drinks.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "shopping") {
        console.log(`🛍️ Using shopping icon for ${place.name}`)
        icon = new L.Icon({
          iconUrl: `/images/shopping.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else {
        console.log(`❓ Unknown place type "${placeType}" for ${place.name}, using default icon`)
        icon = new L.Icon.Default()
      }
    }

    // Create the main marker
    const marker = L.marker([place.lat, place.lng], {
      icon,
    })

    // Add vote count badge if there are votes
    if (voteData && voteData.total > 0) {
      console.log(`🗳️ Adding vote badge to ${place.name} (${voteData.total} votes)`)
      const totalVotes = voteData.total || 0
      const likes = voteData.likes || 0
      const loves = voteData.loves || 0

      // Calculate color based on positive votes ratio
      const positiveVotes = likes + loves
      const ratio = totalVotes > 0 ? positiveVotes / totalVotes : 0

      let backgroundColor = "#ff6b6b" // Red for mostly negative
      if (ratio > 0.6)
        backgroundColor = "#4caf50" // Green for mostly positive
      else if (ratio > 0.3) backgroundColor = "#ffc107" // Yellow for mixed

      // Create a div icon that combines the main marker with vote badge
      const combinedHtml = `
        <div class="marker-with-vote">
          <img src="${icon.options.iconUrl || "/images/default.png"}" class="main-marker-img" />
          <div class="vote-badge" style="background-color: ${backgroundColor}">
            ${totalVotes}
          </div>
        </div>
      `

      const combinedIcon = L.divIcon({
        html: combinedHtml,
        className: "combined-marker",
        iconSize: [41, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
      })

      marker.setIcon(combinedIcon)
    }

    console.log(`✅ Created marker for ${place.name} with icon:`, icon.options?.iconUrl || "default")

    // Add popup with place information
    const popupContent = createPopupContent(place, voteData)
    marker.bindPopup(popupContent)

    return marker
  }

  const createPopupContent = (place, voteData) => {
    let voteInfo = ""
    if (voteData && voteData.total > 0) {
      const likes = voteData.likes || 0
      const loves = voteData.loves || 0
      const skips = voteData.skips || 0

      voteInfo = `
        <div class="popup-votes-simple">
          <div class="vote-counts">
            ${loves > 0 ? `<span class="vote-count-item">❤️ ${loves}</span>` : ""}
            ${likes > 0 ? `<span class="vote-count-item">👍 ${likes}</span>` : ""}
            ${skips > 0 ? `<span class="vote-count-item">👎 ${skips}</span>` : ""}
          </div>
        </div>
      `
    }

    return `
      <div class="place-popup">
        <h3>${place.name}</h3>
        ${voteInfo}
        <div className="popup-actions">
          <button onclick="window.mapViewGoToPlace && window.mapViewGoToPlace('${place.id}')" class="popup-btn">
            📝 View & Vote on Place
          </button>
        </div>
      </div>
    `
  }

  const updateMarkerAppearance = (marker, place) => {
    const placeKey = `${place.lat},${place.lng}`
    const voteData = allVotes[placeKey]
    const placeType = placeTypes[placeKey]

    console.log(`🔄 Updating marker appearance for "${place.name}":`, {
      placeKey,
      placeType,
      hasVoteData: !!voteData,
      voteTotal: voteData?.total || 0,
      showVotes,
    })

    const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"

    // Always start with the appropriate place type icon
    let baseIcon
    if (isLocalhost || !placeType) {
      console.log(`📍 Updating to default icon for ${place.name}`)
      baseIcon = new L.Icon.Default()
    } else {
      // Use custom icons for production
      const cacheParam = isSafari ? `?safari=${Date.now()}&r=${Math.random()}` : `?t=${Date.now()}`

      if (placeType === "restaurant") {
        console.log(`🍽️ Updating to restaurant icon for ${place.name}`)
        baseIcon = new L.Icon({
          iconUrl: `/images/restaurant.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "activity") {
        console.log(`🎯 Updating to activity icon for ${place.name}`)
        baseIcon = new L.Icon({
          iconUrl: `/images/attraction.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "lodging") {
        console.log(`🏨 Updating to lodging icon for ${place.name}`)
        baseIcon = new L.Icon({
          iconUrl: `/images/accommodation.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "nature") {
        console.log(`🌿 Updating to nature icon for ${place.name}`)
        baseIcon = new L.Icon({
          iconUrl: `/images/nature.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "airport") {
        console.log(`✈️ Updating to airport icon for ${place.name}`)
        baseIcon = new L.Icon({
          iconUrl: `/images/airport.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "coffee") {
        console.log(`☕ Updating to coffee icon for ${place.name}`)
        baseIcon = new L.Icon({
          iconUrl: `/images/coffee.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "luggage") {
        console.log(`🧳 Updating to luggage icon for ${place.name}`)
        baseIcon = new L.Icon({
          iconUrl: `/images/luggage.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "drinks") {
        console.log(`🍹 Updating to drinks icon for ${place.name}`)
        baseIcon = new L.Icon({
          iconUrl: `/images/drinks.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "shopping") {
        console.log(`🛍️ Updating to shopping icon for ${place.name}`)
        baseIcon = new L.Icon({
          iconUrl: `/images/shopping.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else {
        console.log(`❓ Unknown place type "${placeType}" for ${place.name}, using default icon`)
        baseIcon = new L.Icon.Default()
      }
    }

    // Add vote badge if there are votes
    if (voteData && voteData.total > 0) {
      console.log(`🗳️ Updating vote badge for ${place.name} (${voteData.total} votes)`)
      const totalVotes = voteData.total || 0
      const likes = voteData.likes || 0
      const loves = voteData.loves || 0

      // Calculate color based on positive votes ratio
      const positiveVotes = likes + loves
      const ratio = totalVotes > 0 ? positiveVotes / totalVotes : 0

      let backgroundColor = "#ff6b6b" // Red for mostly negative
      if (ratio > 0.6)
        backgroundColor = "#4caf50" // Green for mostly positive
      else if (ratio > 0.3) backgroundColor = "#ffc107" // Yellow for mixed

      // Create a div icon that combines the main marker with vote badge
      const combinedHtml = `
        <div class="marker-with-vote">
          <img src="${baseIcon.options.iconUrl || "/images/default.png"}" class="main-marker-img" />
          <div class="vote-badge" style="background-color: ${backgroundColor}">
            ${totalVotes}
          </div>
        </div>
      `

      const combinedIcon = L.divIcon({
        html: combinedHtml,
        className: "combined-marker",
        iconSize: [41, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
      })

      marker.setIcon(combinedIcon)
    } else {
      // No votes, just use the base icon
      marker.setIcon(baseIcon)
    }

    // Update popup content
    const popupContent = createPopupContent(place, voteData)
    marker.setPopupContent(popupContent)
  }

  const handleMapMove = () => {
    if (!mapRef.current) return

    const bounds = mapRef.current.getBounds()
    // Filter based on map bounds - places prop is already filtered by type in parent
    const filtered = places.filter((place) => bounds.contains([place.lat, place.lng]))

    console.log(`🗺️ Map move detected: ${filtered.length}/${places.length} places visible in bounds`)

    setVisiblePlaces(filtered)
    setVisibleCount(filtered.length)
  }

  const handleStartVoting = () => {
    if (visiblePlaces.length === 0) {
      alert("No places visible in current map area. Please zoom out or pan to include some locations.")
      return
    }

    onStartVoting(visiblePlaces)
  }

  const handleGoToPlace = (placeId) => {
    const place = places.find((p) => p.id === placeId)
    if (place && onStartVoting) {
      // Find the index of this place in the places array
      const placeIndex = places.findIndex((p) => p.id === placeId)
      if (placeIndex !== -1) {
        // Store the target index in localStorage so App.js can use it
        localStorage.setItem("triptoi-target-place-index", placeIndex.toString())
        // Start voting with all places
        onStartVoting(places)
      }
    }
  }

  // Make the function available globally for the popup button
  useEffect(() => {
    window.mapViewGoToPlace = handleGoToPlace
    return () => {
      delete window.mapViewGoToPlace
    }
  }, [places, onStartVoting])

  // Add this new useEffect after the existing ones
  useEffect(() => {
    console.log("🔄 Place types changed, updating markers:", placeTypes)

    // Update markers when place types change
    Object.entries(markersRef.current).forEach(([placeId, marker]) => {
      const place = places.find((p) => p.id === placeId)
      if (place) {
        console.log(`🔄 Updating marker for place: ${place.name}`)
        updateMarkerAppearance(marker, place)
      } else {
        console.warn(`⚠️ Could not find place with ID: ${placeId}`)
      }
    })
  }, [placeTypes, places])

  // Force update visible places when places array changes
  useEffect(() => {
    if (mapRef.current && places.length > 0) {
      // Small delay to ensure map is ready
      setTimeout(() => {
        handleMapMove()
      }, 50)
    }
  }, [places])

  return (
    <div className="map-view">
      <div className="map-container-wrapper">
        <div ref={mapContainerRef} className="leaflet-map" />

        {/* Floating map controls */}
        <div className="floating-controls">
          {/* Add location button - NEW */}
          <button onClick={handleAddLocationClick} className="floating-btn add-location-btn" title="Add new location">
            <Plus size={20} style={{ color: "#4caf50" }} />
          </button>

          {/* Hard reset button */}
          <button
            onClick={handleHardReset}
            className="floating-btn show-all-btn"
            title={`Hard reset - bring all ${places.length} places back for voting`}
          >
            <MapPin size={20} style={{ color: "#1976d2" }} />
            <span className="btn-count">{places.length}</span>
          </button>

          {/* Location button */}
          <button
            onClick={handleLocationClick}
            disabled={isLocating}
            className={`floating-btn location-btn ${isShowingUserLocation ? "active" : ""}`}
            title={isShowingUserLocation ? "Show all places" : "My Location"}
          >
            <Navigation
              size={20}
              style={{ color: isShowingUserLocation ? "white" : "#666" }}
              className={isLocating ? "spinning" : ""}
            />
          </button>
        </div>

        {/* Start voting button - always show, disable when no places */}
        <div className="voting-controls">
          <button onClick={handleStartVoting} className="start-voting-btn" disabled={visibleCount === 0}>
            🗳️ Start Voting on These {visibleCount} Location{visibleCount !== 1 ? "s" : ""}
          </button>
        </div>
      </div>

      {/* Add Location Modal */}
      <AddLocationModal
        isOpen={showAddLocationModal}
        onClose={() => setShowAddLocationModal(false)}
        onAddLocation={handleAddLocation}
        isLoading={isAddingLocation}
      />
    </div>
  )
}

export default MapView
